import React, { useEffect, useRef } from 'react';
import { Undo2, X } from 'lucide-react';
import { cn } from '../../lib/utils';

const WorkflowHistoryPanel = ({
  isOpen,
  onClose,
  past = [],
  future = [],
  activeTab = 'undo',
  onTabChange,
  anchorRef,
}) => {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (panelRef.current?.contains(event.target)) return;
      if (anchorRef?.current?.contains(event.target)) return;
      onClose?.();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  const items = activeTab === 'redo' ? future : past;

  return (
    <div
      ref={panelRef}
      className="absolute left-0 top-full z-50 mt-2 w-[320px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
      data-testid="workflow-history-panel"
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-900">Recent changes</h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          data-testid="workflow-history-close"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex border-b border-slate-100">
        <button
          type="button"
          onClick={() => onTabChange?.('undo')}
          className={cn(
            'flex-1 px-4 py-2.5 text-sm font-medium transition-colors',
            activeTab === 'undo'
              ? 'border-b-2 border-emerald-600 text-emerald-600'
              : 'text-slate-500 hover:text-slate-700',
          )}
          data-testid="workflow-history-tab-undo"
        >
          Can Undo ({past.length})
        </button>
        <button
          type="button"
          onClick={() => onTabChange?.('redo')}
          className={cn(
            'flex-1 px-4 py-2.5 text-sm font-medium transition-colors',
            activeTab === 'redo'
              ? 'border-b-2 border-emerald-600 text-emerald-600'
              : 'text-slate-500 hover:text-slate-700',
          )}
          data-testid="workflow-history-tab-redo"
        >
          Can Redo ({future.length})
        </button>
      </div>

      <div className="max-h-72 overflow-y-auto p-2">
        {items.length ? (
          <ul className="space-y-1">
            {[...items].reverse().map((entry, index) => (
              <li
                key={`${entry.label}-${index}`}
                className="rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                {entry.label}
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
              <Undo2 size={22} />
            </div>
            <p className="text-sm font-semibold text-slate-800">
              No {activeTab === 'redo' ? 'redo' : 'undo'} actions available
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Make some changes to see undo history
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowHistoryPanel;
